"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useActiveOrderList = void 0;
const lodash_1 = require("lodash");
const react_1 = require("react");
const operators_1 = require("rxjs/operators");
const trade_utils_lib_1 = require("trade_utils_lib");
const SimpleFuturesAPI_1 = require("../../../apis/SimpleFuturesAPI");
const interface_1 = require("../../../interface");
const common_business_1 = require("@pionex-web-kit/common-business");
const utils_1 = require("../utils");
// 合并 wss 数据至列表
const mergeOrderDataToList = (oldList, order) => {
    const isActiveOrder = (order === null || order === void 0 ? void 0 : order.status) === "open";
    // 关闭的订单删除
    if (isActiveOrder === false) {
        utils_1.SimpleFuturesCloseOrderSubject.next();
        return oldList.filter((ele) => ele.bu_order_id !== order.bu_order_id);
    }
    // 没有订单的情况直接加入
    if (oldList.length <= 0) {
        return [order];
    }
    // 已存在的订单的下标
    const index = oldList.findIndex((ele) => ele.bu_order_id === order.bu_order_id);
    // 是否已经存在订单
    const hasOld = index >= 0;
    // 时间维度 新的加入
    if (!hasOld && order.create_time > oldList[0].create_time) {
        return [order, ...oldList];
    }
    // 时间维度 老的舍弃
    if (!hasOld && order.create_time < oldList[oldList.length - 1].create_time) {
        return oldList;
    }
    // 正常数据合并
    if (hasOld) {
        oldList[index] = Object.assign({}, oldList[index], order);
        return oldList.slice();
    }
    return oldList;
};
/**
 * 活跃仓位列表
 * @param params SimpleFutures.OrderListUIParams
 * @returns SimpleFutures.OrderListUIResponse
 */
const useActiveOrderList = (params) => {
    const [orders, setOrders] = (0, react_1.useState)();
    const [error, setError] = (0, react_1.useState)();
    const [isLastPage, setIsLastPage] = (0, react_1.useState)(false);
    const [isLoadingMore, setIsLoadingMore] = (0, react_1.useState)(false);
    const [isRefreshing, setIsRefreshing] = (0, react_1.useState)(false);
    const refState = (0, react_1.useRef)({});
    const currentParamsRef = (0, react_1.useRef)(params);
    currentParamsRef.current = params;
    /** 清空所有状态 */
    const clear = (0, react_1.useCallback)(() => {
        setOrders(undefined);
        setError(undefined);
        setIsLastPage(false);
        setIsLoadingMore(false);
        setIsRefreshing(false);
        refState.current = {};
    }, []);
    // 登录状态切换至未登录，则清空数据
    (0, common_business_1.useUpdateEffect)(() => {
        if (!params.hasLogin) {
            clear === null || clear === void 0 ? void 0 : clear();
        }
    }, [params.hasLogin]);
    // 首次触发加载一次订单数据
    (0, react_1.useEffect)(() => {
        loadMore();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // 监听活跃仓位 wss、下单 order
    (0, react_1.useEffect)(() => {
        // 监听 wss
        const ssOb$ = (0, trade_utils_lib_1.getTradeDependencyInstance)().getAccountWebSocket().getAllDataObservable();
        const wssSub = ssOb$
            .pipe((0, operators_1.filter)((wssRes) => {
            var _a, _b;
            const wssData = wssRes === null || wssRes === void 0 ? void 0 : wssRes.data;
            const isFutureLiteBu = (wssRes === null || wssRes === void 0 ? void 0 : wssRes.channel) === "bu_order" && (wssData === null || wssData === void 0 ? void 0 : wssData.bu_order_type) === "futures_lite";
            if (!isFutureLiteBu) {
                return false;
            }
            const portfolio = ((_b = (_a = wssData.bu_order_data) === null || _a === void 0 ? void 0 : _a.portfolio) === null || _b === void 0 ? void 0 : _b[0]) || {};
            const isCurrentSymbol = (() => {
                if (!currentParamsRef.current.quote && !currentParamsRef.current.base) {
                    return true;
                }
                return wssData.quote === currentParamsRef.current.quote && portfolio.base === currentParamsRef.current.base;
            })();
            return isCurrentSymbol;
        }), (0, operators_1.map)((res) => utils_1.SimpleFuturesUtils.transferWSSOrderAPIToUI(res.data)))
            .subscribe({
            next: (res) => {
                setOrders((pre) => mergeOrderDataToList(pre !== null && pre !== void 0 ? pre : [], res));
            },
            error: () => { },
        });
        // 监听下单
        const openOrderSub = utils_1.SimpleFuturesOrderSubject.asObservable()
            .pipe((0, operators_1.filter)((res) => {
            var _a;
            const buOrderData = res.bu_order_data || {};
            const portfolio = ((_a = buOrderData.portfolio) === null || _a === void 0 ? void 0 : _a[0]) || {};
            const isCurrentSymbol = (() => {
                if (!currentParamsRef.current.quote && !currentParamsRef.current.base) {
                    return true;
                }
                return res.quote === currentParamsRef.current.quote && portfolio.base === currentParamsRef.current.base;
            })();
            return isCurrentSymbol;
        }), (0, operators_1.map)((res) => utils_1.SimpleFuturesUtils.transferOrderAPIToUI(res)))
            .subscribe({
            next: (res) => {
                setOrders((pre) => {
                    const newOrders = mergeOrderDataToList(pre !== null && pre !== void 0 ? pre : [], res);
                    return newOrders;
                });
            },
            error: () => { },
        });
        return () => {
            wssSub.unsubscribe();
            openOrderSub.unsubscribe();
        };
    }, []);
    // 币对切换，需要先清空请求状态，再刷新数据
    (0, common_business_1.useUpdateEffect)(() => {
        refState.current.isFetching = false;
        const refresh$ = refresh(true);
        return () => {
            refresh$ === null || refresh$ === void 0 ? void 0 : refresh$.unsubscribe();
        };
    }, [params.base, params.quote]);
    // 当切换至前台时，就无静默（无 loading）刷新数据
    (0, common_business_1.useUpdateEffect)(() => {
        if (currentParamsRef.current.isAppFocus) {
            refresh(true);
        }
    }, [currentParamsRef.current.isAppFocus]);
    const getDataObservable = (0, react_1.useCallback)((data) => {
        return SimpleFuturesAPI_1.SimpleFuturesAPI.getOrderList((0, lodash_1.omitBy)({
            page_token: refState.current.nextPageToken,
            status: interface_1.SimpleFutures.OrderStatus.running,
            base: data.base,
            quote: data.quote,
        }, lodash_1.isUndefined)).pipe((0, operators_1.filter)(() => {
            // 过滤掉非当前 symbol 下请求的的币对
            return data.base === currentParamsRef.current.base && data.quote === currentParamsRef.current.quote;
        }));
    }, []);
    /** 加载更多，获取下一页数据 */
    const loadMore = (0, react_1.useCallback)(() => {
        if (refState.current.isFetching || isLastPage || !params.hasLogin) {
            return;
        }
        setIsLoadingMore(true);
        refState.current.isFetching = true;
        return getDataObservable(currentParamsRef.current).subscribe({
            next: (res) => {
                var _a;
                const newData = ((_a = res.results) === null || _a === void 0 ? void 0 : _a.map(utils_1.SimpleFuturesUtils.transferOrderAPIToUI)) || [];
                if (!refState.current.nextPageToken) {
                    setOrders(newData);
                }
                else {
                    setOrders((histories = []) => {
                        return histories === null || histories === void 0 ? void 0 : histories.concat(newData);
                    });
                }
                setIsLastPage(!res.next_page_token);
                refState.current.nextPageToken = res.next_page_token || undefined;
            },
            error: (err) => {
                setError(err);
                setIsLastPage(true);
                setIsLoadingMore(false);
                refState.current.isFetching = false;
            },
            complete: () => {
                setIsLoadingMore(false);
                refState.current.isFetching = false;
            },
        });
    }, [getDataObservable, isLastPage, params.hasLogin]);
    /** 刷新数据 */
    const refresh = (0, react_1.useCallback)((withoutLoading) => {
        if (refState.current.isFetching || !params.hasLogin) {
            return;
        }
        // 清空分页信息
        refState.current.nextPageToken = undefined;
        if (!withoutLoading) {
            setIsRefreshing(true);
        }
        refState.current.isFetching = true;
        return getDataObservable(params).subscribe({
            next: (res) => {
                var _a;
                const newData = ((_a = res.results) === null || _a === void 0 ? void 0 : _a.map(utils_1.SimpleFuturesUtils.transferOrderAPIToUI)) || [];
                setOrders(newData);
                setIsLastPage(!res.next_page_token);
                refState.current.nextPageToken = res.next_page_token || undefined;
            },
            error: (err) => {
                setError(err);
                setIsRefreshing(false);
                refState.current.isFetching = false;
            },
            complete: () => {
                setIsRefreshing(false);
                refState.current.isFetching = false;
            },
        });
    }, [getDataObservable, params]);
    return {
        orders,
        error,
        isLastPage,
        isLoadingMore,
        isRefreshing,
        loadMore,
        refresh,
    };
};
exports.useActiveOrderList = useActiveOrderList;
